/**
 * Self-hosted brand fonts (owner S1). Bundled from node_modules by Vite, with no
 * Google or Adobe CDN. The original loaded the same families from fonts.googleapis.com.
 *
 *  Display  Newsreader, variable, with the opsz axis (6–72) and weight 200–800, upright and italic.
 *           @fontsource-variable declares it as the family "Newsreader Variable".
 *           The type scale sets opsz 72 and weight 360; it stands in for IvyPresto Headline,
 *           which stays first in tokens.type.display so the Adobe kit wins if one is ever added.
 *  Text     Open Sans 400 / 500 / 600 / 700, upright, as in the original.
 *  Devanagari (Tiro Devanagari Marathi, Noto Sans Devanagari) is deferred because v1 is English only.
 *
 * Every file carries unicode-range subsets (latin, latin-ext, …), so the browser fetches
 * only the subsets a page actually uses.
 */
import "@fontsource-variable/newsreader/opsz.css";
import "@fontsource-variable/newsreader/opsz-italic.css";
import "@fontsource/open-sans/400.css";
import "@fontsource/open-sans/500.css";
import "@fontsource/open-sans/600.css";
import "@fontsource/open-sans/700.css";

/** The @font-face family names the self-hosted files declare. */
export const DISPLAY_FACE = "Newsreader Variable";
export const TEXT_FACE = "Open Sans";

/** The faces the first screen needs: display at the type scale's weight, text at body and label weights. */
const CRITICAL_FACES = [`360 72px "${DISPLAY_FACE}"`, `400 15px "${TEXT_FACE}"`, `600 15px "${TEXT_FACE}"`] as const;
/** Latin sample, so only the latin subset is fetched before first paint. The á makes it cover "Ládera". */
const SAMPLE = "Ládera City 0123456789";

let ready: Promise<void> | null = null;

/**
 * Resolves once the display and text faces are loaded, so the loader can reveal
 * the page without a flash of fallback type. It never rejects. It resolves early
 * after `timeoutMs` (slow network, blocked fonts), because the page must still
 * open when the fonts fail. The result is memoised, so calling it again is free.
 */
export function fontsReady(timeoutMs = 3000): Promise<void> {
  if (ready) return ready;
  if (typeof document === "undefined" || !("fonts" in document) || !document.fonts) {
    ready = Promise.resolve();
    return ready;
  }
  const fonts = document.fonts;
  const load = Promise.all(CRITICAL_FACES.map((f) => fonts.load(f, SAMPLE).catch(() => [] as FontFace[]))).then(() => undefined);
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs));
  ready = Promise.race([load, timeout]);
  return ready;
}

/**
 * True when the real IvyPresto Headline (Adobe Fonts kit) has loaded. It is the same
 * test the original ran before flipping data-ivy="true" on the root, which
 * re-tunes display weight and tracking. Without a kit this is always false.
 */
export function ivyPrestoLoaded(): boolean {
  if (typeof document === "undefined" || !("fonts" in document) || !document.fonts) return false;
  try {
    return document.fonts.check('40px "ivypresto-headline"') && [...document.fonts].some((f) => /ivypresto/i.test(f.family) && f.status === "loaded");
  } catch {
    return false;
  }
}
